import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';

/**
 * The session's /api/trader/me read, which confirms the trader's grants: once
 * per sign-in or identity (user/org/role), from the authenticated shell.
 *
 * A failed read leaves grant-gated controls disabled; it is re-tried once each
 * time the trader comes back to the tab, and only while it is failed — an
 * event-driven retry, never a poll (#1041).
 */
export function useProfileRead(): void {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const orgId = useAuthStore((s) => s.user?.orgId ?? null);
  // A sibling tab's role change arrives unconfirmed, like any new identity.
  const role = useAuthStore((s) => s.user?.role ?? null);
  const refreshMe = useAuthStore((s) => s.refreshMe);
  useEffect(() => {
    refreshMe().catch(() => {});
  }, [userId, orgId, role, refreshMe]);

  const failed = useAuthStore((s) => s.user?.grantsPending === 'error');
  useEffect(() => {
    if (!failed) return;
    const retry = () => {
      if (document.visibilityState === 'visible') refreshMe().catch(() => {});
    };
    window.addEventListener('focus', retry);
    document.addEventListener('visibilitychange', retry);
    return () => {
      window.removeEventListener('focus', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }, [failed, refreshMe]);
}
